import type { useAgreementDocumentsTab } from '../../hooks/useAgreementDocumentsTab';
import { Alert, Button } from '@shared/components';
import { DOCUMENT_TYPE_LABELS } from '@/features/document-templates/utils/documentTypeLabels';
import { formatOccurredAt } from '../../utils/timelineEntryLabels';
import { AgreementDocumentsTab } from './AgreementDocumentsTab';
import { DocumentDetailsModal } from './DocumentDetailsModal';
import { DocumentViewerModal } from './DocumentViewerModal';
import { GenerateDocumentModal } from './GenerateDocumentModal';

interface Props {
    documents: ReturnType<typeof useAgreementDocumentsTab>;
}

/** The tab with the modals it opens, wired to the hook's state. */
export function AgreementDocumentsSection({ documents }: Props) {
    const { generate, details, file } = documents;
    const viewing = file.viewing;
    const viewDocument = file.onView && ((document: Parameters<NonNullable<typeof file.onView>>[0]) => {
        details.close();
        file.onView?.(document);
    });
    const downloadDocument = file.onDownload && ((document: Parameters<NonNullable<typeof file.onDownload>>[0]) => {
        details.close();
        file.onDownload?.(document);
    });

    return (
        <>
            {file.downloadError && !viewing && (
                <Alert variant="error" className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <span>{file.downloadError}</span>
                    <Button type="button" size="xs" variant="danger" appearance="outline" className="shrink-0" onClick={file.dismissDownloadError}>
                        Dismiss
                    </Button>
                </Alert>
            )}
            <AgreementDocumentsTab
                status={documents.status}
                page={documents.page}
                currentPage={documents.currentPage}
                now={documents.now}
                canGenerate={documents.canGenerate}
                canRead={documents.canRead}
                onGenerate={generate.open}
                onView={file.onView}
                onDownload={file.onDownload}
                onDetails={details.open}
                onRetry={documents.onRetry}
                onRefresh={documents.onRefresh}
                onPageChange={documents.onPageChange}
            />
            <GenerateDocumentModal
                isOpen={generate.isOpen}
                documentType={generate.form.documentType}
                fieldError={generate.form.fieldError}
                formError={generate.form.formError}
                isSubmitting={generate.form.isSubmitting}
                onDocumentTypeChange={generate.form.setDocumentType}
                onSubmit={generate.form.submit}
                onClose={generate.close}
            />
            <DocumentDetailsModal
                document={details.document}
                now={documents.now}
                canRead={documents.canRead}
                onView={viewDocument}
                onDownload={downloadDocument}
                onClose={details.close}
            />
            <DocumentViewerModal
                isOpen={viewing !== null}
                title={viewing ? DOCUMENT_TYPE_LABELS[viewing.document.documentType] : ''}
                subtitle={viewing?.document.generatedAt ? `Generated ${formatOccurredAt(viewing.document.generatedAt)}` : undefined}
                status={viewing?.error ? 'error' : viewing?.fileUrl ? 'ready' : 'loading'}
                fileUrl={viewing?.fileUrl ?? undefined}
                errorMessage={viewing?.error ?? undefined}
                downloadError={file.downloadError ?? undefined}
                canDownload={documents.canRead}
                isDownloading={file.isDownloading}
                onDownload={() => viewing && file.download(viewing.document)}
                onClose={file.closeViewer}
            />
        </>
    );
}
